import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  FileText, Upload, Mail, Copy, CheckCircle2, Clock, AlertTriangle,
  ArrowRight, Sparkles, Building2, Receipt, Coins, ArrowUpRight,
  ShieldCheck, XCircle, Send, RefreshCw, Loader2, Users,
} from "lucide-react";
import CompanySwitcher from "@/components/CompanySwitcher";
import CommercialistaDashboard from "@/pages/CommercialistaDashboard";
import InboundStatusCard from "@/components/InboundStatusCard";
import FicOnboardingBanner from "@/components/FicOnboardingBanner";
import { toast } from "sonner";

const STATUS_LABELS = {
  uploaded: { label: "Caricata", color: "bg-[#F5F2EA] text-[#4A4A4A] border-[#E8E4D9]", icon: FileText },
  extracting: { label: "In estrazione", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  extracted: { label: "Estratta", color: "bg-blue-50 text-blue-700 border-blue-200", icon: Sparkles },
  reviewed: { label: "Verificata", color: "bg-blue-50 text-blue-700 border-blue-200", icon: CheckCircle2 },
  xml_generated: { label: "XML pronto", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  error: { label: "Errore", color: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
};

// Mirrors backend openapi_sdi.SDI_STATUS_LABELS, kept in sync with the API
// response `labels` (fallback if the endpoint has not answered yet).
const SDI_STATUS_LABELS = {
  RC: { label: "Consegnata", tone: "success" },
  MC: { label: "Mancata consegna", tone: "warning" },
  NS: { label: "Scartata", tone: "danger" },
  DT: { label: "Termini decorsi", tone: "muted" },
  NE: { label: "Esito committente", tone: "info" },
  EC: { label: "Esito committente", tone: "info" },
  AT: { label: "Attestazione", tone: "info" },
  pending: { label: "In transito", tone: "info" },
};

export default function Dashboard() {
  useSeo({
    title: "Dashboard",
    description: "Panoramica delle tue autofatture estere: stato SDI, crediti disponibili, ultime elaborazioni.",
    path: "/app",
    noindex: true,
  });
  const { user, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [company, setCompany] = useState(null);
  const [sdi, setSdi] = useState(null);
  const [refreshingSdi, setRefreshingSdi] = useState(false);
  const [credits, setCredits] = useState(null);

  // Refresh user from server on mount so is_commercialista / email_verified
  // reflect the latest admin toggle without requiring re-login.
  useEffect(() => { refresh?.(); }, [refresh]);

  const fetchAll = async () => {
    const [s, inv, comp, sdiData, cr] = await Promise.all([
      api.get("/invoices/stats"),
      api.get("/invoices?limit=5"),
      api.get("/company"),
      api.get("/dashboard/sdi-summary").catch(() => ({ data: null })),
      api.get("/credits/balance").catch(() => ({ data: null })),
    ]);
    setStats(s.data);
    // Support both shapes: legacy array (backward compat) and paginated
    // `{items, total, page, limit, pages}` (Feb 29, 2026 v12).
    const invList = Array.isArray(inv.data) ? inv.data : (inv.data?.items || []);
    setInvoices(invList.slice(0, 5));
    setCompany(comp.data);
    setSdi(sdiData.data);
    setCredits(cr.data);
  };

  // On mount + on every silent reload, ask the backend to poll OpenAPI on
  // ALL invoices with sdi_uuid (not just pending). This keeps the UI live
  // even for invoices that transitioned to a final state (RC/NS/MC) via a
  // manual action on the OpenAPI console.
  const refreshAllSdi = async () => {
    try {
      setRefreshingSdi(true);
      const { data } = await api.post("/invoices/refresh-all-sdi");
      // If any status transitioned, re-fetch the invoice list + summary so
      // banners/badges everywhere pick up the change.
      if (data?.updates > 0) {
        await fetchAll();
      } else {
        const fresh = await api.get("/dashboard/sdi-summary");
        setSdi(fresh.data);
      }
    } catch (e) {
      console.warn("[Dashboard] SDI refresh failed", e);
    } finally {
      setRefreshingSdi(false);
    }
  };

  useEffect(() => {
    // Commercialisti see the dedicated CommercialistaDashboard below — skip
    // the expensive fetchAll + refresh-all-sdi (which polls OpenAPI for
    // every sent invoice across ALL clients and can take 15+ seconds).
    // Still load the global credits wallet — that's studio-wide, cheap, and
    // the CommercialistaDashboard header shows it.
    if (user?.is_commercialista) {
      const loadCredits = () => {
        api.get("/credits/balance")
          .then(({ data }) => setCredits(data))
          .catch(() => {});
      };
      loadCredits();
      const t = setInterval(loadCredits, 30000);
      return () => clearInterval(t);
    }
    // Fetch dati locali. Dal Feb 2026 non facciamo più polling periodico
    // di OpenAPI: il webhook `customer-notification` ci notifica i cambi
    // di stato in tempo reale. Un solo `refreshAllSdi` al mount come
    // safety-net per fatture appena inviate e non ancora ricevute via
    // webhook (che tipicamente arriva in 10-60s).
    fetchAll().then(() => refreshAllSdi());
    // Nessun setInterval → zero chiamate ricorrenti a OpenAPI.
  }, [user?.is_commercialista]);

  // Prefer the company-scoped inbound (assigned per azienda). Fallback to
  // the legacy per-user address only during the first render before
  // /company returns.
  const inboundEmail = company?.inbound_email || user?.inbound_email || "";

  // Commercialisti see a dedicated multi-client dashboard.
  if (user?.is_commercialista) {
    return <CommercialistaDashboard user={user} credits={credits} />;
  }

  const copyEmail = () => {
    if (inboundEmail) {
      navigator.clipboard.writeText(inboundEmail);
      toast.success("Email copiata negli appunti");
    }
  };

  const needsProfile = company && !company.partita_iva;
  const firstName = user?.full_name?.split(" ")[0] || "";

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="dashboard-page">
      {/* Hero header */}
      <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-3">
            <span className="w-6 h-px bg-[#1D4ED8]" />
            {user?.is_commercialista ? "Studio commercialista \u00b7 Panoramica" : "Panoramica \u00b7 Oggi"}
          </div>
          <h1
            className="font-display font-extrabold text-4xl lg:text-5xl tracking-tighter leading-[0.95] text-[#0A0A0A]"
            data-testid="dashboard-title"
          >
            Ciao <span className="font-serif-accent text-[#1D4ED8]">{firstName}</span>
          </h1>
          <p className="text-[#4A4A4A] mt-3 max-w-md">
            {user?.is_commercialista
              ? "Stai gestendo pi\u00f9 clienti sotto un unico account. Cambia cliente attivo dal selettore in sidebar"
              : "Ecco cosa \u00e8 successo alle tue autofatture estere"}
          </p>
          {user?.is_commercialista && company && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800" data-testid="dashboard-active-company-chip">
              <Building2 className="w-3.5 h-3.5" />
              <span className="text-xs font-semibold">Cliente attivo:</span>
              <span className="text-xs font-mono">{company.denominazione}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/app/account/credits"
            data-testid="dashboard-credits-btn"
            title="Vedi e acquista crediti"
            className={`group inline-flex items-center gap-2 px-4 py-3 rounded-full text-sm font-semibold transition-all border ${
              credits && credits.credits_available <= 5
                ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
                : "bg-white text-[#0A0A0A] border-[#E8E4D9] hover:bg-[#F5F2EA]"
            }`}
          >
            <Coins className={`w-4 h-4 ${credits && credits.credits_available <= 5 ? "text-amber-700" : "text-[#1D4ED8]"}`} />
            <span className="tabular-nums font-mono">
              {credits === null ? "…" : credits.credits_available}
            </span>
            <span className="text-[10px] uppercase tracking-wider opacity-70 hidden sm:inline">crediti</span>
            {credits && credits.credits_available <= 5 && (
              <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 border border-amber-300 rounded px-1.5 py-0.5 hidden md:inline">
                ricarica
              </span>
            )}
          </Link>
          <Link
            to="/app/upload"
            data-testid="dashboard-new-btn"
            className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] shadow-lg shadow-black/10 transition-all"
          >
            <Upload className="w-4 h-4" />
            Nuova autofattura
            <span className="w-5 h-5 rounded-full bg-white/15 grid place-items-center group-hover:translate-x-0.5 transition-transform">
              <ArrowRight className="w-3 h-3" />
            </span>
          </Link>
        </div>
      </div>

      {/* FIC onboarding banner + inbound setup card */}
      <FicOnboardingBanner />
      <div className="mb-5">
        <InboundStatusCard />
      </div>

      {needsProfile && (
        <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3" data-testid="profile-warning">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-amber-900">Manca la tua Partita IVA</div>
            <div className="text-sm text-amber-800 mt-0.5">Serve per generare gli XML conformi allo SDI</div>
          </div>
          <Link
            to="/app/company"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-amber-900 text-white text-sm font-semibold hover:bg-amber-800 transition-colors"
            data-testid="profile-warning-cta"
          >
            Configura <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      <EmailVerificationBanner user={user} />

      {/* Stats — editorial big numbers */}
      <div className={`grid grid-cols-1 ${user?.is_commercialista ? "md:grid-cols-4" : "md:grid-cols-3"} gap-4 mb-8`}>
        {user?.is_commercialista && (
          <ClientsStat testid="stat-clients" />
        )}
        <BigStat
          kicker={user?.is_commercialista ? "Fatture (cliente attivo)" : "Fatture totali"}
          value={stats?.total ?? 0}
          icon={Receipt}
          accent="blue"
          testid="stat-total"
        />
        <BigStat
          kicker="XML generati"
          value={stats?.by_status?.xml_generated ?? 0}
          icon={CheckCircle2}
          accent="emerald"
          testid="stat-xml"
          serif
        />
        <BigStat
          kicker="Importo estero"
          value={stats ? formatEur(stats.total_amount_eur) : "€ 0,00"}
          icon={Coins}
          accent="coral"
          testid="stat-amount"
          serif
        />
      </div>

      {/* Split section: Inbound email only (quick action cards removed) */}
      <div className="grid grid-cols-1 gap-4 mb-10">
        <div className="rounded-2xl border border-[#E8E4D9] bg-white p-6" data-testid="inbound-email-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#1D4ED8]/10 grid place-items-center">
              <Mail className="w-5 h-5 text-[#1D4ED8]" />
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-tight">Email per ricevere fatture</div>
              <div className="text-sm text-[#737373] mt-0.5">Inoltra qui i PDF di Google, Meta, Amazon</div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-[#F5F2EA] border border-[#E8E4D9] rounded-xl">
            <span className="font-mono text-sm text-[#0A0A0A] flex-1 truncate" data-testid="inbound-email-value">
              {inboundEmail || "\u2014"}
            </span>
            <button
              onClick={copyEmail}
              data-testid="copy-inbound-email-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-white border border-[#E8E4D9] hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"
            >
              <Copy className="w-3.5 h-3.5" /> Copia
            </button>
          </div>
        </div>
      </div>

      {/* SDI section — visible only if at least one invoice has been sent to SDI */}
      {sdi && sdi.total > 0 && (
        <div className="mb-10" data-testid="sdi-summary-section">
          <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-1">
                Sistema di Interscambio
              </div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight text-[#0A0A0A]">
                Stato invii <span className="font-serif-accent text-[#1D4ED8]">SDI</span>
              </h2>
              <p className="text-xs text-[#737373] mt-1">
                Clicca un contatore per vedere l&apos;elenco filtrato.
              </p>
            </div>
            {refreshingSdi && (
              <div className="inline-flex items-center gap-2 text-xs text-[#737373]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sincronizzo con SDI…
              </div>
            )}
          </div>

          {/* Status counters row — clickable, each links to filtered invoice list */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sdi-counters">
            <SdiCounter code="RC" count={sdi.counts?.RC || 0} icon={ShieldCheck} tone="success" />
            <SdiCounter code="pending" count={sdi.counts?.pending || 0} icon={Send} tone="info" />
            <SdiCounter code="NS" count={sdi.counts?.NS || 0} icon={XCircle} tone="danger" />
            <SdiCounter code="MC" count={sdi.counts?.MC || 0} icon={AlertTriangle} tone="warning" />
            <SdiCounter code="DT" count={sdi.counts?.DT || 0} icon={Clock} tone="muted" />
          </div>
        </div>
      )}

      {/* Recent invoices as table */}
      <div>
        <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-1">Ultimi movimenti</div>
            <h2 className="font-display font-extrabold text-2xl tracking-tight text-[#0A0A0A]">Le tue ultime <span className="font-serif-accent text-[#F97316]">autofatture</span></h2>
          </div>
          <Link to="/app/invoices" className="text-sm text-[#1D4ED8] hover:underline font-semibold inline-flex items-center gap-1" data-testid="see-all-invoices">
            Vedi tutte <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {invoices.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E8E4D9] bg-white p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#F5F2EA] grid place-items-center mx-auto mb-4">
              <FileText className="w-6 h-6 text-[#737373]" />
            </div>
            <div className="font-display font-bold text-xl text-[#0A0A0A]">Nessuna fattura ancora</div>
            <p className="text-sm text-[#4A4A4A] mt-1 max-w-sm mx-auto">Carica la tua prima fattura estera e in 12 secondi hai l&apos;XML.</p>
            <Link
              to="/app/upload"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors"
              data-testid="empty-upload-cta"
            >
              Carica il primo PDF <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="recent-invoices-table">
              <caption className="sr-only">Ultime autofatture ricevute</caption>
              <thead>
                <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
                  <th scope="col" className="text-left px-5 py-3 font-bold">Fornitore</th>
                  <th scope="col" className="text-left px-5 py-3 font-bold hidden md:table-cell w-24">Documento</th>
                  <th scope="col" className="text-left px-5 py-3 font-bold hidden md:table-cell w-32">Stato</th>
                  <th scope="col" className="text-left px-5 py-3 font-bold hidden lg:table-cell w-36">Stato SDI</th>
                  <th scope="col" className="text-right px-5 py-3 font-bold w-40">Importo</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <tr
                    key={inv.id}
                    data-testid={`recent-invoice-${i}`}
                    onClick={(e) => {
                      // Let real anchor clicks bubble; otherwise navigate via Link inside cell.
                      const a = e.currentTarget.querySelector("a");
                      if (a) a.click();
                    }}
                    className={`cursor-pointer hover:bg-[#F5F2EA]/60 transition-colors ${
                      i > 0 ? "border-t border-[#E8E4D9]/70" : ""
                    }`}
                  >
                    <td className="px-5 py-4 min-w-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-[#F5F2EA] grid place-items-center shrink-0 border border-[#E8E4D9]">
                          <FileText className="w-4 h-4 text-[#4A4A4A]" />
                        </div>
                        <div className="min-w-0">
                          <Link
                            to={`/app/invoices/${inv.id}`}
                            className="font-semibold text-[#0A0A0A] hover:text-[#1D4ED8] truncate block"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {inv.extracted?.supplier_name || inv.file_name}
                          </Link>
                          <div className="text-xs text-[#737373] font-mono truncate">
                            {inv.extracted?.invoice_number || "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <span className="text-xs font-mono font-bold text-[#1D4ED8]">{inv.document_type}</span>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      {inv.lock_state?.manual_transmission ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider bg-[#F5F2EA] text-[#0A0A0A] border-[#0A0A0A]/40"
                          data-testid="dashboard-sdi-manual"
                        >
                          Trasmessa manualmente
                        </span>
                      ) : inv.sdi_uuid ? (
                        <SdiStatusBadge code={inv.sdi_status} labelsFromServer={sdi?.labels} />
                      ) : (
                        <span className="text-[10px] italic text-[#A3A3A3]">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right font-mono font-semibold text-[#0A0A0A] tabular-nums whitespace-nowrap">
                      {inv.extracted?.total_amount
                        ? `${(inv.extracted?.currency || "EUR")} ${inv.extracted.total_amount.toFixed(2)}`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ClientsStat({ testid }) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    api.get("/companies").then(({ data }) => setCount((data || []).length)).catch(() => setCount(0));
  }, []);
  return (
    <div
      className="rounded-2xl border border-[#E8E4D9] bg-gradient-to-br from-[#1D4ED8]/5 to-white p-6 hover:shadow-lg transition-shadow"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-xl bg-[#1D4ED8]/10 text-[#1D4ED8] grid place-items-center">
          <Users className="w-5 h-5" />
        </div>
        <div className="w-1.5 h-1.5 rounded-full bg-[#1D4ED8] animate-pulse" />
      </div>
      <div className="mt-6 text-[10px] uppercase tracking-[0.25em] text-[#737373] font-bold">Clienti gestiti</div>
      <div className="mt-1 font-display font-extrabold text-4xl md:text-5xl tracking-tighter leading-none text-[#0A0A0A]">
        {count === null ? "\u2026" : count}
      </div>
    </div>
  );
}

function BigStat({ kicker, value, icon: Icon, accent, testid, serif }) {  const iconBg = {
    blue: "bg-[#1D4ED8]/10 text-[#1D4ED8]",
    emerald: "bg-[#059669]/10 text-[#059669]",
    coral: "bg-[#F97316]/10 text-[#F97316]",
  }[accent];
  const dot = {
    blue: "bg-[#1D4ED8]",
    emerald: "bg-[#059669]",
    coral: "bg-[#F97316]",
  }[accent];
  return (
    <div
      className="rounded-2xl border border-[#E8E4D9] bg-white p-6 hover:shadow-lg transition-shadow"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl ${iconBg} grid place-items-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className={`w-1.5 h-1.5 rounded-full ${dot} animate-pulse`} />
      </div>
      <div className="mt-6 text-[10px] uppercase tracking-[0.25em] text-[#737373] font-bold">{kicker}</div>
      <div className={`mt-1 font-display font-extrabold text-4xl md:text-5xl tracking-tighter leading-none text-[#0A0A0A] ${serif ? "" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function formatEur(n) {
  if (n == null) return "€ 0,00";
  return `€ ${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.uploaded;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${s.color}`}
      data-testid={`status-${status}`}
    >
      <Icon className="w-3 h-3" />
      {s.label}
    </span>
  );
}

function SdiCounter({ code, count, icon: Icon, tone }) {
  const meta = SDI_STATUS_LABELS[code] || SDI_STATUS_LABELS.pending;
  const styles = {
    success: { icon: "bg-[#059669]/10 text-[#059669]", dot: "bg-[#059669]", ring: "hover:ring-[#059669]/30" },
    danger:  { icon: "bg-red-100 text-red-600", dot: "bg-red-500", ring: "hover:ring-red-400/40" },
    warning: { icon: "bg-amber-100 text-amber-700", dot: "bg-amber-500", ring: "hover:ring-amber-400/40" },
    info:    { icon: "bg-[#1D4ED8]/10 text-[#1D4ED8]", dot: "bg-[#1D4ED8]", ring: "hover:ring-[#1D4ED8]/30" },
    muted:   { icon: "bg-[#F5F2EA] text-[#4A4A4A]", dot: "bg-[#4A4A4A]", ring: "hover:ring-[#4A4A4A]/30" },
  }[tone] || { icon: "bg-[#F5F2EA] text-[#4A4A4A]", dot: "bg-[#4A4A4A]", ring: "hover:ring-[#4A4A4A]/30" };
  // Ogni contatore linka al filtro corrispondente sulla lista fatture.
  // `code` è già il valore che il backend accetta come `?status=sdi:<code>`.
  const href = `/app/invoices?status=sdi:${code}`;
  return (
    <Link
      to={href}
      className={`group relative rounded-2xl border border-[#E8E4D9] bg-white p-4 transition-all hover:shadow-md hover:-translate-y-0.5 hover:ring-2 ${styles.ring} focus:outline-none focus:ring-2 focus:ring-[#1D4ED8] focus:ring-offset-2`}
      data-testid={`sdi-counter-${code}`}
      aria-label={`${meta.label}: ${count} fatture. Clicca per aprire la lista filtrata.`}
    >
      <div className="flex items-center justify-between">
        <div className={`w-9 h-9 rounded-xl ${styles.icon} grid place-items-center`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className={`w-1.5 h-1.5 rounded-full ${styles.dot} ${count > 0 ? "animate-pulse" : "opacity-40"}`} />
      </div>
      <div className="mt-3 text-[9px] uppercase tracking-[0.2em] text-[#737373] font-bold flex items-center gap-1">
        {meta.label}
        <ArrowRight className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
      </div>
      <div className="mt-0.5 font-display font-extrabold text-3xl tracking-tighter leading-none text-[#0A0A0A] tabular-nums">
        {count}
      </div>
    </Link>
  );
}

function SdiStatusBadge({ code, labelsFromServer }) {
  const c = (code || "pending").toUpperCase();
  const meta = SDI_STATUS_LABELS[c] || SDI_STATUS_LABELS.pending;
  const label = labelsFromServer?.[c] || meta.label;
  const cls = {
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-red-50 text-red-700 border-red-200",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
    muted: "bg-[#F5F2EA] text-[#4A4A4A] border-[#E8E4D9]",
  }[meta.tone] || "bg-blue-50 text-blue-700 border-blue-200";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${cls}`}
      data-testid={`sdi-status-${c}`}
    >
      {label}
    </span>
  );
}

function EmailVerificationBanner({ user }) {
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user || user.email_verified !== false || dismissed) return null;

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/verify-email/resend");
      toast.success("Email di verifica inviata: controlla la casella");
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="mb-8 rounded-2xl border border-blue-200 bg-blue-50 p-5 flex items-start gap-3"
      data-testid="email-verify-banner"
    >
      <Mail className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-blue-900">Verifica il tuo indirizzo email</div>
        <div className="text-sm text-blue-800 mt-0.5">
          Ti abbiamo inviato un link di verifica a <strong>{user.email}</strong>. Controlla la casella (anche lo spam)
        </div>
      </div>
      <button
        onClick={resend}
        disabled={resending || sent}
        data-testid="email-verify-resend-btn"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-900 text-white text-sm font-semibold hover:bg-blue-800 disabled:opacity-60 transition-colors"
      >
        {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {sent ? "Inviato" : "Reinvia email"}
      </button>
    </div>
  );
}
